import { Injectable } from '@angular/core';
import {
    CorrespondencePreferenceType,
    Country,
    DuoCertificateType,
    ListedCertificate,
    MentionType,
    Nationality,
    ProfessionalGroup,
    ProfessionalGroupValidity,
    ProfessionalPosition,
    ReferenceDataApiService,
    SpecialismType,
    SystemParameter
} from '@zorro/apiclient';
import { List } from 'linqts';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ReferenceDataService {
    private countries: Observable<Country[]>;
    private professionalGroups: Observable<ProfessionalGroup[]>;
    private nationalities: Observable<Nationality[]>;
    private mentionTypes: Observable<MentionType[]>;
    private professionalGroupValidities: Observable<ProfessionalGroupValidity[]>;
    private specialismTypes: Observable<SpecialismType[]>;
    private correspondencePreferenceTypes: Observable<CorrespondencePreferenceType[]>;
    private listedCertificates: Observable<ListedCertificate[]>;
    private systemParameters: Observable<SystemParameter[]>;
    private professionalPositions: Observable<ProfessionalPosition[]>;
    private duoCertificateTypes: Observable<DuoCertificateType[]>;

    constructor(private readonly referenceDataApiService: ReferenceDataApiService) { }

    private static cacheResult<T>(observable: Observable<T>) {
        return from(observable.toPromise());
    }

    getProfessionalGroups(): Observable<ProfessionalGroup[]> {
        if (!this.professionalGroups) {
            this.professionalGroups = ReferenceDataService.cacheResult(
                this.sort(this.referenceDataApiService.professionalGroups(), p => p.description.toLowerCase())
            );
        }
        return this.professionalGroups;
    }

    getCountries(validTo: Date = null): Observable<Country[]> {
        if (!this.countries) {
            this.countries = ReferenceDataService.cacheResult(this.referenceDataApiService.countries());
        }
        return this.countries
            .pipe(map(countries => {
                if (validTo === null) {
                    return countries;
                }
                return countries.filter(c => {
                    return c.validTo === null || c.validTo >= validTo;
                });
            }));
    }

    getNationalities(): Observable<Nationality[]> {
        if (!this.nationalities) {
            this.nationalities = ReferenceDataService.cacheResult(this.referenceDataApiService.nationalities());
        }
        return this.nationalities;
    }

    getProfessionalGroupValidities(): Observable<ProfessionalGroupValidity[]> {
        if (!this.professionalGroupValidities) {
            this.professionalGroupValidities = ReferenceDataService.cacheResult(this.referenceDataApiService.professionalGroupValidities());
        }
        return this.professionalGroupValidities;
    }

    getSpecialismTypes(): Observable<SpecialismType[]> {
        if (!this.specialismTypes) {
            this.specialismTypes = ReferenceDataService.cacheResult(
                this.sort(this.referenceDataApiService.specialismTypes(), s => s.description.toLowerCase())
            );
        }
        return this.specialismTypes;
    }

    getMentionTypes(): Observable<MentionType[]> {
        if (!this.mentionTypes) {
            this.mentionTypes = ReferenceDataService.cacheResult(
                this.sort(this.referenceDataApiService.mentionTypes(), mt => mt.description.toLowerCase())
            );
        }
        return this.mentionTypes;
    }

    // getActiveCountries(): Observable<Country[]> {
    //     return this.getCountries(DateExtension.today());
    // }

    // /*
    //  *
    //  * @param validTo: when null: all countries, else countries that where valid from and up validTo
    //  */
    // getCountries(validTo: Date = null): Observable<Country[]> {
    //     if (!this.countries) {
    //         this.countries = ReferenceDataService.cacheResult(this.referenceDataApiService.countries());
    //     }
    //     return this.countries
    //         .pipe(CloneOperators.cloneMap<Country>())
    //         .pipe(map(countries => {
    //             if (validTo === null) {
    //                 return countries;
    //             }
    //             return countries.filter(c => {
    //                 return (c.validTo === null || new DateExtension(c.validTo).date() >= new DateExtension(validTo).date());
    //             });
    //         }));
    // }

    // getNationalities(): Observable<Nationality[]> {
    //     if (!this.nationalities) {
    //         this.nationalities = ReferenceDataService.cacheResult(this.referenceDataApiService.nationalities());
    //     }
    //     return this.nationalities.pipe(CloneOperators.cloneMap<Nationality>());
    // }

    // getValidProfessionalGroups(): Observable<ProfessionalGroup[]> {
    //     const professionalGroups$ = this.getProfessionalGroups();
    //     const validities$ = this.getProfessionalGroupValidities();

    //     return combineLatest([
    //         professionalGroups$,
    //         validities$]).pipe(
    //             map(([professionalGroups, validities]) => {
    //                 const now = new Date();
    //                 const professionValidities = validities.filter(v =>
    //                     // * tpg_id = 1 (Artikel 3 – Register) OF 3 (Artikel 36a - Tijdelijk register);
    //                     // * pgtv_StartdatumAanvragen =< vandaag;
    //                     // * pgtv_EindDatum >= vandaag OF = leeg
    //                     (new Date(v.startDate) <= now ||
    //                         (v.applicationStartDate != null && new Date(v.applicationStartDate) <= now)) &&
    //                     (v.endDate == null || new Date(v.endDate) >= now) &&
    //                     (v.professionalGroupType.valueOf() === ProfessionalGroupType.artikel3.valueOf() ||
    //                         v.professionalGroupType.valueOf() === ProfessionalGroupType.artikel36A.valueOf())
    //                 );
    //                 return professionalGroups.filter(p => professionValidities.find(v => v.professionalGroupCode === p.code) != null);
    //             }));
    // }

    // getCorrespondencePreferenceTypes(): Observable<CorrespondencePreferenceType[]> {
    //     if (!this.correspondencePreferenceTypes) {
    //         this.correspondencePreferenceTypes = ReferenceDataService
    //             .cacheResult(this.referenceDataApiService.correspondencePreferenceTypes());
    //     }
    //     return this.correspondencePreferenceTypes.pipe(CloneOperators.cloneMap<CorrespondencePreferenceType>());
    // }

    getListedCertificates(): Observable<ListedCertificate[]> {
        if (!this.listedCertificates) {
            this.listedCertificates = ReferenceDataService.cacheResult(this.referenceDataApiService.listedCertificates());
        }
        return this.listedCertificates;
    }

    // getSystemParameters(): Observable<SystemParameter[]> {
    //     if (!this.systemParameters) {
    //         this.systemParameters = ReferenceDataService.cacheResult(this.referenceDataApiService.systemParameters());
    //     }

    //     return this.systemParameters.pipe(CloneOperators.cloneMap<SystemParameter>());
    // }

    // getProfessionalPositions(): Observable<ProfessionalPosition[]> {
    //     if (!this.professionalPositions) {
    //         this.professionalPositions = ReferenceDataService.cacheResult(this.referenceDataApiService.professionalPositions());
    //     }

    //     return this.professionalPositions.pipe(CloneOperators.cloneMap<ProfessionalPosition>());
    // }

    // getDuoCertificateTypes(): Observable<DuoCertificateType[]> {
    //     if (!this.duoCertificateTypes) {
    //         this.duoCertificateTypes = ReferenceDataService.cacheResult(this.referenceDataApiService.duoCertificateTypes());
    //     }

    //     return this.duoCertificateTypes.pipe(CloneOperators.cloneMap<DuoCertificateType>());
    // }

    private sort<T>(observable: Observable<Array<T>>, selector: (key: T) => any): Observable<Array<T>> {
        return observable.pipe(map(a => new List(a).OrderBy(selector).ToArray()));
    }
}
